/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { array } from 'prop-types';
import mbpLogger from '@module-logger/mbp';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import cookies from '../../../../../helpers/common/storage/cookies';
import SiteWideBannerMultiOffer from './SiteWideBannerMultiOffer';
import { getOfferInboxData, getPageType } from '../../../../../../state/ducks/App/App-Selectors';
import useIsPlaPage from '../../../../../helpers/hooks/useIsPlaPage';
import useIsDisplayChannel from '../../../../../helpers/hooks/useIsDisplayChannel';
import isProductListingAd from '../../../../../helpers/isProductListingAd';
import useUTMRules from '../../../../../helpers/DynamicRules/hooks/useUTMRules';
import useSalesforceOfferGatewayQuery from '../../../../../helpers/Personalization/useSalesforceOfferGatewayQuery';
import useClickStreamCustomEventsTracking from '../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';

const SiteWideMultiOfferBannerContainer = ({ bannerData }) => {
    const pageType = useSelector(getPageType);
    const location = useLocation();
    const currentUrl = location?.pathname;
    const isPlaPage = useIsPlaPage();
    const isDisplayChannel = useIsDisplayChannel();
    const isPlaPagesEnabled = isPlaPage || isDisplayChannel || isProductListingAd(location?.search);
    const persistedUserProfile = cookies.get('persisted_user_profile');
    const offerInboxABVariant = useSelector(getOfferInboxData)?.abTestVariant;
    const shouldRenderOfferInbox = (!offerInboxABVariant || offerInboxABVariant === 'OfferInbox');

    const interactionName = bannerData?.salesforce_campaign_id;
    if (!bannerData?.reference?.length && !interactionName?.length) return null;

    const targeting = {
        pageType: 'home',
    };

    const {
        error,
        data,
    } = useSalesforceOfferGatewayQuery({
        queryName: 'SiteWideOfferGateway',
        interactionName,
        targeting,
        featureFlag: interactionName?.length > 0 && shouldRenderOfferInbox,
        isSessionHasAccount: !!persistedUserProfile,
        optInHoldoutParticipation: true,
        resolveIdentity: true,
    });

    if (error) {
        mbpLogger.logError({
            function: `SiteWideMultiOfferBannerContainer - interactionName = ${interactionName}`,
            message: 'Error loading API data',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error,
        });
        // render default banner with bannerData
    }

    const sfData = data?.offerGatewayCampaignSF;
    const sfCampaign = sfData?.campaign;

    const trackClickThrough = useClickStreamCustomEventsTracking({
        salesforceResponse: sfData,
        isFireImpression: !!sfCampaign,
        page: 'home',
    });

    const multiBannerData = sfData?.content || bannerData?.reference?.[0] || {};

    const inclusionExclusionData = multiBannerData?.multibanner_options?.inclusions_exclusions?.[0] || {};
    if (!inclusionExclusionData || Object.keys(inclusionExclusionData).length === 0 || !inclusionExclusionData.type) {
        return <SiteWideBannerMultiOffer multiBannerData={multiBannerData} trackClickThrough={trackClickThrough} />;
    }

    if (!inclusionExclusionData?.type || !pageType) return null;

    const configuredPageTypes = Array.isArray(inclusionExclusionData.page_types)
        ? inclusionExclusionData.page_types.map((p) => p.toLowerCase())
        : [];

    const pageTypeMatches = configuredPageTypes.includes(pageType?.toLowerCase())
        || (configuredPageTypes.includes('plas') && isPlaPagesEnabled);

    const normalize = (url) => {
        if (!url) return '';
        const clean = url?.trim()?.replace?.(/,+$/, '');
        return clean === '/' ? '/' : clean?.replace?.(/\/+$/, '');
    };

    const isUrlMatched = () => {
        if (!Array.isArray(inclusionExclusionData?.urls) || !currentUrl) return false;
        const normalizedCurrent = normalize(currentUrl);
        return inclusionExclusionData.urls.some((group) => {
            const includeAll = !!group.include_all;
            const urlList = Array.isArray(group.url_group)
                ? group.url_group.flatMap((u) => u
                    .split(',')
                    .map((part) => normalize(part))
                    .filter(Boolean),
                )
                : [];
            return urlList.some((url) => (includeAll ? normalizedCurrent.includes(url) : normalizedCurrent === url),
            );
        });
    };

    const type = inclusionExclusionData?.type?.toLowerCase();

    let shouldShowBanner = false;
    if (type === 'exclusion') {
        shouldShowBanner = !(pageTypeMatches || isUrlMatched());
    } else if (type === 'inclusion') {
        shouldShowBanner = pageTypeMatches || isUrlMatched();
    }
    const utmPassed = useUTMRules(multiBannerData?.multibanner_options);

    if (!utmPassed || !shouldShowBanner) {
        return <></>;
    }

    const isOfferBoxEnabled = !!(sfData?.content && multiBannerData?.enable_offer_inbox);

    return <SiteWideBannerMultiOffer multiBannerData={multiBannerData} trackClickThrough={trackClickThrough} isOfferBoxEnabled={isOfferBoxEnabled} sfCampaign={sfCampaign} />;
};

SiteWideMultiOfferBannerContainer.propTypes = {
    bannerData: array.isRequired,
};

export default SiteWideMultiOfferBannerContainer;
